import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { useMediaDeviceSelect } from "@livekit/components-react";

/**
 * Which microphone, or which camera.
 *
 * Built from `useMediaDeviceSelect` rather than LiveKit's `MediaDeviceMenu`,
 * which draws its own button and its own dropdown — so putting it inside a
 * menu of ours produced a menu that opened a menu, and its labels are
 * hardcoded English besides.
 *
 * The active device is marked with a tick as well as a fill, because "which
 * microphone am I on" is not a question to answer by comparing two greys
 * while someone is waiting for you to reply.
 */
export default function MeetingDeviceList({
    kind,
    onSelected,
}: {
    kind: MediaDeviceKind;
    /** Picking a device is a one-shot, so the menu goes away with the choice. */
    onSelected: () => void;
}) {
    const { t } = useTranslation();
    const { devices, activeDeviceId, setActiveMediaDevice } = useMediaDeviceSelect({
        kind,
    });

    if (devices.length === 0) {
        return (
            <p className="px-2 py-1.5 text-xs leading-relaxed text-room-ink-muted">
                {t("meetingControls.noDevices")}
            </p>
        );
    }

    return (
        <ul>
            {devices.map((device, index) => {
                const active = device.deviceId === activeDeviceId;
                return (
                    <li key={device.deviceId || index}>
                        <button
                            type="button"
                            onClick={() => {
                                void setActiveMediaDevice(device.deviceId);
                                onSelected();
                            }}
                            aria-pressed={active}
                            className={`room-menu-item ${active ? "bg-room-active" : ""}`}
                        >
                            <span className="flex w-4 shrink-0 justify-center">
                                {active && <Check size={14} aria-hidden="true" />}
                            </span>
                            {/* A browser withholds the label until permission is
                                given, and "" would render as an empty row. */}
                            <span className="truncate">
                                {device.label ||
                                    t("meetingControls.unnamedDevice", {
                                        number: index + 1,
                                    })}
                            </span>
                        </button>
                    </li>
                );
            })}
        </ul>
    );
}
